// mds-dojo — dijit/DialogUnderlay — FAITHFUL port of references/dijit/DialogUnderlay.js.
// AMD → ESM + declare from the faithful runtime. Blocks input behind a dijit/Dialog.
// Unused `dojo/aspect` import dropped. `dojo/window` → ../dojo/window (winUtils);
// `dojo/sniff` not needed. Statics DialogUnderlay.show/.hide + _singleton preserved.
import declare from '../dojo/_base/declare-runtime';
import lang from '../dojo/_base/lang';
import domAttr from '../dojo/dom-attr';
import domStyle from '../dojo/dom-style';
import on from '../dojo/on';
import winUtils from '../dojo/window';
import _Widget from './_Widget';
import _TemplatedMixin from './_TemplatedMixin';
import BackgroundIframe from './BackgroundIframe';
import Viewport from './Viewport';
import dijit from './main'; // for back-compat, exporting dijit._underlay (remove in 2.0)

var DialogUnderlay: any = declare('dijit.DialogUnderlay', [_Widget, _TemplatedMixin], {
  // summary:
  //		A component used to block input behind a `dijit/Dialog`.

  // Template has two divs; outer div is used for fade-in/fade-out, and also to hold background iframe.
  // Inner div has opacity specified in CSS file.
  templateString: "<div class='dijitDialogUnderlayWrapper'><div class='dijitDialogUnderlay' tabIndex='-1' data-dojo-attach-point='node'></div></div>",

  // dialogId: String
  //		Id of the dialog.... DialogUnderlay's id is based on this id
  dialogId: '',

  // class: String
  //		This class name is used on the DialogUnderlay node, in addition to dijitDialogUnderlay
  'class': '',

  // This will get overwritten as soon as show() is call, but leave an empty array in case hide() or destroy()
  // is called first.   The array is shared between instances but that's OK because we never write into it.
  _modalConnects: [],

  _setDialogIdAttr: function (this: any, id: any) {
    domAttr.set(this.node, 'id', id + '_underlay');
    this._set('dialogId', id);
  },

  _setClassAttr: function (this: any, clazz: any) {
    this.node.className = 'dijitDialogUnderlay ' + clazz;
    this._set('class', clazz);
  },

  postCreate: function (this: any) {
    // Append the underlay to the body
    this.ownerDocumentBody.appendChild(this.domNode);

    this.own(on(this.domNode, 'keydown', lang.hitch(this, '_onKeyDown')));

    this.inherited(arguments);
  },

  layout: function (this: any) {
    // summary:
    //		Sets the background to the size of the viewport

    var is = this.node.style,
      os = this.domNode.style;

    // hide the background temporarily, so that the background itself isn't
    // causing scrollbars to appear (might happen when user shrinks browser
    // window and then we are called to resize)
    os.display = 'none';

    // then resize and show
    var viewport = winUtils.getBox(this.ownerDocument);
    os.top = viewport.t + 'px';
    os.left = viewport.l + 'px';
    is.width = viewport.w + 'px';
    is.height = viewport.h + 'px';
    os.display = 'block';
  },

  show: function (this: any) {
    // summary:
    //		Show the dialog underlay
    this.domNode.style.display = 'block';
    this.open = true;
    this.layout();
    this.bgIframe = new (BackgroundIframe as any)(this.domNode);

    var win = winUtils.get(this.ownerDocument);
    this._modalConnects = [
      Viewport.on('resize', lang.hitch(this, 'layout')),
      on(win, 'scroll', lang.hitch(this, 'layout'))
    ];

  },

  hide: function (this: any) {
    // summary:
    //		Hides the dialog underlay

    this.bgIframe.destroy();
    delete this.bgIframe;
    this.domNode.style.display = 'none';
    while (this._modalConnects.length) { (this._modalConnects.pop()).remove(); }
    this.open = false;
  },

  destroy: function (this: any) {
    while (this._modalConnects.length) { (this._modalConnects.pop()).remove(); }
    this.inherited(arguments);
  },

  _onKeyDown: function () {
    // summary:
    //		Extension point so Dialog can monitor keyboard events on the underlay.
  }
});

DialogUnderlay.show = function (/*Object*/ attrs: any, /*Number*/ zIndex: any) {
  // summary:
  //		Display the underlay with the given attributes set.  If the underlay is already displayed,
  //		then adjust it's attributes as specified.

  var underlay = DialogUnderlay._singleton;
  if (!underlay || underlay._destroyed) {
    underlay = dijit._underlay = DialogUnderlay._singleton = new DialogUnderlay(attrs);
  } else {
    if (attrs) { underlay.set(attrs); }
  }
  domStyle.set(underlay.domNode, 'zIndex', zIndex);
  if (!underlay.open) {
    underlay.show();
  }
};

DialogUnderlay.hide = function () {
  // summary:
  //		Hide the underlay.

  // Guard code in case the underlay widget has already been destroyed
  // because we are being called during page unload (when all widgets are destroyed)
  var underlay = DialogUnderlay._singleton;
  if (underlay && !underlay._destroyed) {
    underlay.hide();
  }
};

export default DialogUnderlay;
